import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { STATUS_ORDER, STATUS, LOST_REASONS } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

export function StatusChangeDialog({ open, onOpenChange, lead, onSaved }) {
  const [status, setStatus] = useState(lead?.status || "neu");
  const [lostReason, setLostReason] = useState("kein_interesse");
  const [actualValue, setActualValue] = useState(lead?.potential_value || "");
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { status };
      if (status === "verloren") payload.lost_reason = lostReason;
      if (status === "kunde") payload.actual_value = parseFloat(actualValue) || 0;
      await api.patch(`/leads/${lead.id}/status`, payload);
      toast.success("Status aktualisiert");
      qc.invalidateQueries();
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="status-change-dialog">
        <DialogHeader>
          <DialogTitle>Status ändern</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">{lead.company}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {STATUS_ORDER.map((s) => (
              <button key={s} type="button" data-testid={`status-option-${s}`}
                onClick={() => setStatus(s)}
                className={cn("rounded-lg border px-3 py-2 text-sm font-medium transition-colors text-left",
                  status === s ? STATUS[s].color : "border-border text-muted-foreground hover:bg-accent")}>
                {STATUS[s].label}
              </button>
            ))}
          </div>

          {status === "verloren" && (
            <div>
              <Label>Ablehnungsgrund</Label>
              <Select value={lostReason} onValueChange={setLostReason}>
                <SelectTrigger data-testid="lost-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOST_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {status === "kunde" && (
            <div>
              <Label>Tatsächlicher Auftragswert (€)</Label>
              <Input data-testid="actual-value-input" type="number" value={actualValue}
                onChange={(e) => setActualValue(e.target.value)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} data-testid="status-save-btn" disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
